import { faUserPlus } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import PropTypes from 'prop-types';
import React, { useCallback, useEffect, useState } from 'react';
import { useForm, Controller } from 'react-hook-form';

import {
    Button,
    Checkbox,
    DialogActions,
    DialogContent,
    FormControlLabel,
    Grid,
    ListItemText,
    TextField
} from '@mui/material';
import {
    FormLine, RequiredSelectHack, StyledListItem
} from 'components';
import { SelectLabel } from 'components/Text';
import { FormHelper, translate } from 'utils';

import debounce from 'debounce-promise';
import { useStores } from 'hooks';
import AsyncSelect from 'react-select/async';

import { CompanyService } from 'services';
import { HeaderModal } from './_HeaderModal';

export const CreateUserModal = ({
                                    onClose, onConfirm, closeOnSubmit
                                }) => {
    const {
        control,
        handleSubmit,
        watch,
        formState: { errors },
        setValue,
        clearErrors
    } = useForm({
        defaultValues: {
            accountActivated: false,
            mainCompany: null,
            roles: [],
            email: null,
            firstName: '',
            lastName: '',
            contactEmails: '',
            login: '',
            password: ''
        }
    });

    const { userStore } = useStores();
    const { scope } = userStore;
    const [selectedCompany, setSelectedCompany] = useState(null);
    const accountActivated = watch('accountActivated');

    useEffect(() => {
        if (scope?.company) {
            setSelectedCompany(scope?.company);
        }
    }, [scope]);


    useEffect(() => {
        if (!accountActivated) {
            clearErrors('login');
        }
    }, [accountActivated, clearErrors]);

    const handleAddCompany = useCallback(value => {
        setSelectedCompany(value);
    }, []);


    const onSubmit = (data) => {
        const emailErrors = errors.email;

        if (!emailErrors) {
            onConfirm({
                login: data.login,
                password: data.password,
                person: {
                    firstName: data.firstName,
                    lastName: data.lastName,
                    mainCompany: selectedCompany,
                    contact: {
                        contactEmails: data.contactEmails,
                        technicalEmail: data.email
                    }
                },
                accountActivated: data.accountActivated
            });
        }

        if (closeOnSubmit) {
            onClose();
        }
    };

    const getAsyncCompaniesOptions = useCallback(inputValue => new Promise(resolve => {
        CompanyService
            .getCompanyOptions({ freeSearch: inputValue })
            .then(response => resolve(response));
    }), []);

    const debouncedCompaniesLoadOptions = debounce(getAsyncCompaniesOptions, 500);

    const CustomOption = ({ data, innerProps }) => (
        <StyledListItem {...innerProps}>
            <ListItemText primary={data.label} />
        </StyledListItem>
    );

    return (
        <form autoComplete="off" onSubmit={handleSubmit(onSubmit)}>
            <HeaderModal onClose={onClose}>
                <>
                    <FontAwesomeIcon icon={faUserPlus} />
                    {translate('modalUser.newUser')}
                </>
            </HeaderModal>

            <DialogContent>
                <Grid container direction="column" style={{ minWidth: 400 }}>
                    <Controller
                        control={control}
                        name="accountActivated"
                        render={({ field }) => (
                            <FormControlLabel
                                control={<Checkbox color="primary" {...field} checked={field.value} />}
                                label={translate('common.userAccount')}
                            />
                        )}
                    />

                    <Controller
                        control={control}
                        name="lastName"
                        render={({ field }) => (
                            <TextField
                                {...field}
                                autoComplete="off"
                                autoFocus
                                error={!!errors.lastName}
                                label={translate('common.lastName')}
                                required
                            />
                        )}
                        rules={{ required: true }}
                    />

                    <Controller
                        control={control}
                        name="firstName"
                        render={({ field }) => (
                            <TextField
                                {...field}
                                autoComplete="new-password"
                                error={!!errors.firstName}
                                label={translate('common.firstName')}
                                required
                            />
                        )}
                        rules={{ required: true }}
                    />

                    <Controller
                        control={control}
                        name="contactEmails"
                        render={({ field }) => (
                            <TextField
                                {...field}
                                autoComplete="new-password"
                                error={!!errors.contactEmails}
                                helperText={errors.contactEmails?.message}
                                label={translate('common.contactEmails.modal')}
                            />
                        )}
                        rules={{
                            validate: value => FormHelper.validateEmails(value)
                        }}
                    />

                    <SelectLabel>{translate('common.company')}</SelectLabel>
                    <FormLine>
                        <AsyncSelect
                            cacheOptions
                            closeMenuOnSelect
                            components={{ Option: CustomOption }}
                            defaultOptions
                            isClearable
                            loadingMessage={() => translate('common.loading')}
                            loadOptions={inputValue => debouncedCompaniesLoadOptions(inputValue)}
                            menuPortalTarget={document.body}
                            name="companies"
                            noOptionsMessage={() => translate('errors.noOptions')}
                            placeholder={translate('common.searchByInstitution')}
                            required
                            styles={{
                                menu: base => ({ ...base, zIndex: 2000 }),
                                menuPortal: basePortal => ({ ...basePortal, zIndex: 2000 })
                            }}
                            value={selectedCompany}
                            onChange={handleAddCompany}
                        />
                    </FormLine>
                    <RequiredSelectHack value={selectedCompany || ''} />

                    {accountActivated && (
                        <>
                            <Controller
                                control={control}
                                name="email"
                                render={({ field }) => (
                                    <TextField
                                        {...field}
                                        autoComplete="new-password"
                                        error={!!errors.email}
                                        helperText={errors.email?.message}
                                        label={translate('common.userModal.technicalEmail')}
                                        required
                                        type="email"
                                    />
                                )}
                                rules={{
                                    required: accountActivated,
                                    validate: value => FormHelper.validateEmail(value)
                                }}
                            />

                            {/* This input is only used for forcing disabling autocomplete in chrome browser */}
                            <input name="fakeInputForForcingDisablingAutocompleteChrome3" style={{ display: 'none' }} type="text" />

                            <Controller
                                control={control}
                                name="login"
                                render={({ field }) => (
                                    <TextField
                                        {...field}
                                        autoComplete="new-password"
                                        disabled={!accountActivated}
                                        error={!!errors.login}
                                        label={translate('common.login')}
                                        required={accountActivated}
                                    />
                                )}
                                rules={{ required: accountActivated }}
                            />
                        </>
                    )}
                </Grid>
            </DialogContent>

            <DialogActions>
                <Button onClick={onClose}>{translate('button.cancel')}</Button>
                <Button color="primary" type="submit">{translate('common.create')}</Button>
            </DialogActions>
        </form>
    );
};

CreateUserModal.propTypes = {
    closeOnSubmit: PropTypes.bool,
    onClose: PropTypes.func.isRequired,
    onConfirm: PropTypes.func.isRequired
};

CreateUserModal.defaultProps = {
    closeOnSubmit: true
};